import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { Store } from '@ngrx/store';
import { switchMap, take, filter, map, tap } from 'rxjs/operators';

import { HttpService } from './http.service';
import {
  TagsListResponse,
  Tag,
  TagSubmitPayload,
  DEFAULT_TAGS_CURRENT_PAGE,
  DEFAULT_TAGS_PER_PAGE,
} from '../../store/entities/models/tag.model';
import { selectCurrentDomainId } from '../../store/entities/domains/domains.selectors';
import { transformSnakeToCamel, transformCamelToSnake } from '../../store/utils/transformers';
import { ApiTableFilter } from '../../shared/models/table-filter.model';

export interface TagsListParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort?: string;
  sort_direction?: string;
  filters?: ApiTableFilter[];
  filter_link?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TagsService {
  private http = inject(HttpService);
  private store = inject(Store);
  private readonly tagsListCache = new Map<string, Observable<TagsListResponse>>();

  private getDomainId$(): Observable<string> {
    return this.store.select(selectCurrentDomainId).pipe(
      filter((id): id is string => id !== null),
      take(1),
    );
  }

  getTagsList(params: TagsListParams = {}): Observable<TagsListResponse> {
    return this.getDomainId$().pipe(
      switchMap((domainId) => this.getCachedTagsList(domainId, params))
    );
  }

  createTag(payload: TagSubmitPayload): Observable<{ data: Tag }> {
    return this.getDomainId$().pipe(
      switchMap((domainId) =>
        this.http
          .post<{ data: Tag }>(`/${domainId}/tags`, transformCamelToSnake(payload) as Record<string, unknown>)
          .pipe(tap(() => this.invalidateTags(domainId)))
      )
    );
  }

  updateTag(tagId: string, payload: TagSubmitPayload): Observable<{ data: Tag }> {
    return this.getDomainId$().pipe(
      switchMap((domainId) =>
        this.http
          .put<{ data: Tag }>(`/${domainId}/tags/${tagId}`, transformCamelToSnake(payload) as Record<string, unknown>)
          .pipe(tap(() => this.invalidateTags(domainId)))
      )
    );
  }

  deleteTag(tagId: string): Observable<void> {
    return this.getDomainId$().pipe(
      switchMap((domainId) =>
        this.http
          .delete<void>(`/${domainId}/tags/${tagId}`)
          .pipe(tap(() => this.invalidateTags(domainId)))
      )
    );
  }

  /** Clear every cached tag query for a domain after tag create/update/delete. */
  invalidateTags(domainId: string): void {
    for (const key of this.tagsListCache.keys()) {
      if (key.startsWith(`${domainId}:`)) {
        this.tagsListCache.delete(key);
      }
    }
  }

  private getCachedTagsList(domainId: string, params: TagsListParams): Observable<TagsListResponse> {
    const queryParams: Record<string, unknown> = {
      page: params.page ?? DEFAULT_TAGS_CURRENT_PAGE,
      per_page: params.per_page ?? DEFAULT_TAGS_PER_PAGE,
      sort: params.sort ?? 'created_at',
      sort_direction: params.sort_direction ?? 'desc',
      search: params.search ?? '',
    };

    if (params.filters?.length) {
      queryParams.filters = params.filters;
      queryParams.filter_link = params.filter_link ?? 'and';
    }

    const cacheKey = `${domainId}:${JSON.stringify(queryParams)}`;
    const cached = this.tagsListCache.get(cacheKey);

    if (cached) {
      return cached;
    }

    const request$ = this.http
      .get<Record<string, unknown>>(`/${domainId}/tags`, queryParams)
      .pipe(
        map((response) => transformSnakeToCamel(response) as TagsListResponse),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.tagsListCache.set(cacheKey, request$);

    return request$;
  }
}
